//src/pages/Community/CreatePostModal.js
import React, { useState } from "react";
import "./css/CreatePostModal.css";
import { db, storage } from "../../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { awardXP, XP_RULES } from "../../services/xpService";
import { logFirebaseError, isPermissionDenied } from "../../utils/fbError";

const CreatePostModal = ({ user, onClose, onPostCreated }) => {
  const [tab, setTab] = useState("blip");
  const [content, setContent] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const applyFormat = (prefix, suffix) => {
    const textarea = document.getElementById("markdown-input");
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.slice(start, end);
    const updated =
      content.slice(0, start) + prefix + selected + suffix + content.slice(end);

    setContent(updated);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const applyLinePrefix = (prefix) => {
    const textarea = document.getElementById("markdown-input");
    const lines = content.split("\n");
    const cursorLine =
      textarea.value.substr(0, textarea.selectionStart).split("\n").length - 1;
    lines[cursorLine] = prefix + lines[cursorLine];
    setContent(lines.join("\n"));
    setTimeout(() => textarea.focus(), 0);
  };

  const validate = () => {
    const trimmed = content.trim();

    if (!trimmed) {
      setValidationError(
        `${tab.charAt(0).toUpperCase() + tab.slice(1)} cannot be empty.`
      );
      return false;
    }

    if (tab === "blip") {
      if (trimmed.length < 10) {
        setValidationError("Blip must be at least 10 characters.");
        return false;
      }
      if (trimmed.length > 100) {
        setValidationError("Blip cannot exceed 100 characters.");
        return false;
      }
    }

    if (tab === "question") {
      if (trimmed.length < 10) {
        setValidationError("Question must be at least 10 characters.");
        return false;
      }
      if (trimmed.length > 400) {
        setValidationError("Question cannot exceed 400 characters.");
        return false;
      }
    }

    if (tab === "poll") {
      if (trimmed.length < 10) {
        setValidationError("Poll question must be at least 10 characters.");
        return false;
      }
      if (trimmed.length > 200) {
        setValidationError("Poll question cannot exceed 200 characters.");
        return false;
      }

      const validOptions = pollOptions.filter((opt) => opt.trim());
      if (validOptions.length < 2) {
        setValidationError("Poll must have at least 2 non-empty options.");
        return false;
      }
    }

    setValidationError("");
    return true;
  };

  const handlePost = async () => {
    if (!user) {
      setValidationError("Please log in to create a post.");
      return;
    }

    if (!validate()) return;

    setIsPosting(true);
    let imageUrl = null;

    try {
      if (imageFile) {
        const imageRef = ref(
          storage,
          `community_images/${uuidv4()}_${imageFile.name}`
        );
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      const newPost = {
        type: tab,
        content: content.trim(),
        image: imageUrl || null,
        timestamp: serverTimestamp(),
        userId: user.uid,
        likes: [],
      };

      if (tab === "poll") {
        newPost.options = pollOptions
          .filter((opt) => opt.trim())
          .map((text) => ({ text, votes: 0 }));
      }

      const docRef = await addDoc(collection(db, "community_posts"), newPost);
      onPostCreated({
        ...newPost,
        id: docRef.id,
        // Use a local Date so the UI can render immediately
        timestamp: new Date(),
        // optional niceties for the feed:
        commentCount: 0,
      });

      // ✅ Award XP for creating a post (blip/question/poll)
      try {
        const eventType =
          tab === "blip"
            ? "CREATE_POST_BLIP"
            : tab === "question"
            ? "CREATE_POST_QUESTION"
            : "CREATE_POST_POLL";

        // awardXP(uid, type, dedupeKey, meta?)
        await awardXP(user.uid, XP_RULES.CREATE_POST, eventType, docRef.id, {
          postType: tab,
        });
      } catch (e) {
        logFirebaseError("CreatePostModal.awardXP", e, {
          postId: docRef.id,
          tab,
        });
        if (isPermissionDenied(e)) {
          // Don’t block the post—just inform user XP wasn’t added
          console.warn(
            "XP not granted due to permissions. The post was created successfully."
          );
        }
      }

      onClose();
    } catch (err) {
      logFirebaseError("CreatePostModal.handlePost", err, {
        tab,
        hasImage: !!imageFile,
      });
      if (isPermissionDenied(err)) {
        setValidationError(
          "You don't have permission to post. Please contact admin."
        );
      } else {
        alert("Failed to post.");
      }
    } finally {
      setIsPosting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="create-modal-overlay" onClick={onClose}>
      <div
        className="create-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Create Post</h3>
        <div className="create-tabs">
          {["blip", "question", "poll"].map((t) => (
            <button
              key={t}
              className={tab === t ? "active" : ""}
              onClick={() => {
                setTab(t);
                setValidationError("");
              }}
            >
              {t === "blip"
                ? "🐦 Blip"
                : t === "question"
                ? "💬 Question"
                : "🗳 Poll"}
            </button>
          ))}
        </div>

        <div className="markdown-toolbar">
          <button type="button" onClick={() => applyFormat("**", "**")}>
            B
          </button>
          <button type="button" onClick={() => applyFormat("_", "_")}>
            I
          </button>
          <button type="button" onClick={() => applyLinePrefix("- ")}>
            • List
          </button>
          {/* <button type="button" onClick={() => applyLinePrefix("## ")}>
            ## Heading
          </button> */}
        </div>

        <textarea
          id="markdown-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={
            tab === "blip"
              ? "Share a short thought..."
              : tab === "question"
              ? "Ask a question using formatting if needed..."
              : "Enter your poll question..."
          }
          maxLength={
            tab === "blip"
              ? 100
              : tab === "question"
              ? 400
              : tab === "poll"
              ? 200
              : undefined
          }
        />

        {validationError && <div className="error-msg">{validationError}</div>}

        {tab === "poll" && (
          <div className="poll-options">
            {pollOptions.map((opt, idx) => (
              <input
                key={idx}
                type="text"
                placeholder={`Option ${idx + 1}`}
                value={opt}
                onChange={(e) => {
                  const updated = [...pollOptions];
                  updated[idx] = e.target.value;
                  setPollOptions(updated);
                }}
              />
            ))}
            {pollOptions.length < 4 && (
              <button
                className="add-option-btn"
                onClick={() => setPollOptions([...pollOptions, ""])}
                type="button"
                disabled={isPosting}
              >
                ＋
              </button>
            )}
          </div>
        )}

        {!imagePreview ? (
          <label className="upload-label">
            <span className="upload-button">📷 Attach Image</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isPosting}
              hidden
            />
          </label>
        ) : (
          <div className="upload-preview-wrapper">
            <div className="upload-status">
              <span className="attached-message">✅ Image Attached</span>
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                }}
                disabled={isPosting}
              >
                ❌ Remove
              </button>
            </div>
            <div className="image-preview">
              <img src={imagePreview} alt="Preview" />
            </div>
          </div>
        )}

        <button onClick={handlePost} className="post-btn" disabled={isPosting}>
          {isPosting ? "Posting..." : "Post"}
        </button>

        <button className="close-btn" onClick={onClose}>
          ✖ Close
        </button>
      </div>
    </div>
  );
};

export default CreatePostModal;
